import en from './translations/en.json';
import ar from './translations/ar.json';
import de from './translations/de.json';
import es from './translations/es.json';
import fr from './translations/fr.json';
import hi from './translations/hi.json';
import id from './translations/id.json';
import it from './translations/it.json';
import ja from './translations/ja.json';
import ko from './translations/ko.json';
import nl from './translations/nl.json';
import pt from './translations/pt.json';
import uk from './translations/uk.json';
import vi from './translations/vi.json';
import zh from './translations/zh.json';

export type Locale = 'en' | 'ar' | 'de' | 'es' | 'fr' | 'hi' | 'id' | 'it' | 'ja' | 'ko' | 'nl' | 'pt' | 'uk' | 'vi' | 'zh';

/** Deep merge: target keys take precedence; missing keys filled from en */
function mergeWithEn<T extends object>(base: T, overrides: Partial<T>): T {
  const out = { ...base } as T;
  for (const key of Object.keys(overrides) as (keyof T)[]) {
    const b = (base as Record<string, unknown>)[key as string];
    const o = (overrides as Record<string, unknown>)[key as string];
    if (o != null && typeof o === 'object' && !Array.isArray(o) && typeof b === 'object' && b != null && !Array.isArray(b)) {
      (out as Record<string, unknown>)[key as string] = mergeWithEn(b as object, o as object);
    } else if (o !== undefined) {
      (out as Record<string, unknown>)[key as string] = o;
    }
  }
  return out;
}

export const translations: Record<Locale, typeof en> = {
  en,
  ar: mergeWithEn(en, ar as unknown as Partial<typeof en>),
  de: mergeWithEn(en, de as unknown as Partial<typeof en>),
  es: mergeWithEn(en, es as unknown as Partial<typeof en>),
  fr: mergeWithEn(en, fr as unknown as Partial<typeof en>),
  hi: mergeWithEn(en, hi as unknown as Partial<typeof en>),
  id: mergeWithEn(en, id as unknown as Partial<typeof en>),
  it: mergeWithEn(en, it as unknown as Partial<typeof en>),
  ja: mergeWithEn(en, ja as unknown as Partial<typeof en>),
  ko: mergeWithEn(en, ko as unknown as Partial<typeof en>),
  nl: mergeWithEn(en, nl as unknown as Partial<typeof en>),
  pt: mergeWithEn(en, pt as unknown as Partial<typeof en>),
  uk: mergeWithEn(en, uk as unknown as Partial<typeof en>),
  vi: mergeWithEn(en, vi as unknown as Partial<typeof en>),
  zh: mergeWithEn(en, zh as unknown as Partial<typeof en>),
};

export const localeNames: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  hi: 'हिन्दी',
  id: 'Bahasa Indonesia',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  nl: 'Nederlands',
  pt: 'Português',
  uk: 'Українська',
  vi: 'Tiếng Việt',
  zh: '中文',
};

export const supportedLocales: Locale[] = ['en', 'ar', 'de', 'es', 'fr', 'hi', 'id', 'it', 'ja', 'ko', 'nl', 'pt', 'uk', 'vi', 'zh'];

/**
 * Parse an HTTP `Accept-Language` header value and return the closest
 * supported locale. Falls back to 'en' when nothing matches. Safe to call
 * server-side (no DOM access).
 */
export function detectLocaleFromHeader(acceptLanguage: string): Locale {
  const parts = acceptLanguage.split(',');
  for (const part of parts) {
    const tag = part.split(';')[0].trim().toLowerCase();
    const primary = tag.split('-')[0];
    if (supportedLocales.includes(primary as Locale)) {
      return primary as Locale;
    }
  }
  return 'en';
}

/**
 * Resolve any locale-ish input (explicit locale code OR Accept-Language
 * header string) into a supported `Locale`. Defaults to 'en' when input is
 * null/undefined/unrecognized. This is the canonical server-side helper
 * for code that wants a single `Locale` to feed into `getTranslation`.
 */
export function resolveLocale(input?: string | null): Locale {
  if (!input) return 'en';
  const trimmed = input.trim();
  if (!trimmed) return 'en';
  if (supportedLocales.includes(trimmed as Locale)) {
    return trimmed as Locale;
  }
  return detectLocaleFromHeader(trimmed);
}

/**
 * Detect browser locale and return closest supported locale.
 * Priority: ?lang= URL param > localStorage > browser language > 'en'
 */
export function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  
  // Check URL search params for ?lang= override
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && supportedLocales.includes(langParam as Locale)) {
      // Persist the URL-specified locale so it sticks across navigation
      localStorage.setItem('locale', langParam);
      return langParam as Locale;
    }
  } catch {
    // Ignore URL parsing errors
  }

  // Check localStorage for user preference
  const stored = localStorage.getItem('locale');
  if (stored && supportedLocales.includes(stored as Locale)) {
    return stored as Locale;
  }
  
  // Get browser languages
  const browserLangs = navigator.languages || [navigator.language];
  
  for (const lang of browserLangs) {
    // Get the primary language code (e.g., 'en' from 'en-US')
    const primary = lang.split('-')[0].toLowerCase();
    
    if (supportedLocales.includes(primary as Locale)) {
      return primary as Locale;
    }
  }
  
  return 'en';
}

/**
 * Get nested translation value by dot-notation key
 */
function getNestedValue(obj: unknown, keys: string[]): string | null {
  let value: unknown = obj;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = (value as Record<string, unknown>)[k];
    } else {
      return null;
    }
  }
  
  return typeof value === 'string' ? value : null;
}

export function getTranslation(
  localeTranslations: typeof en,
  key: string,
  params?: Record<string, string | number>
): string {
  const keys = key.split('.');
  
  // Try current locale first
  let value = getNestedValue(localeTranslations, keys);
  
  // Fallback to English if not found
  if (value === null && localeTranslations !== en) {
    value = getNestedValue(en, keys);
  }
  
  // Return key if still not found
  if (value === null) {
    return key;
  }
  
  // Replace parameters like {count}
  if (params) {
    return value.replace(/\{(\w+)\}/g, (_, paramKey) => 
      String(params[paramKey] ?? `{${paramKey}}`)
    );
  }
  
  return value;
}
